import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Chart from "react-apexcharts";
import api from "../utils/api";
import ChartBurgerMenu from "./ChartBurgerMenu";
import AIAnalyzerModal from "./AIAnalyzerModal";
import AIAnalyzerButton from "./AIAnalyzerButton";

import FieldHelp from "../help/FieldHelp";
import { buildChartPayload } from "../utils/aiAnalyzerEngine";
import {
  formatAmount,
  resolveTariffs,
  unitLabel,
} from "../utils/currencyConversion";

function parseTimeStrToHours(timeStr) {
  if (!timeStr) return 0;
  let hours = 0;
  const hrMatch = timeStr.match(/(\d+)\s*hr/i);
  if (hrMatch) hours += parseInt(hrMatch[1], 10);
  const minMatch = timeStr.match(/(\d+)\s*min/i);
  if (minMatch) hours += parseInt(minMatch[1], 10) / 60;
  return Number(hours.toFixed(2));
}

// Format decimal hours -> "X hrs Y mins" (mirrors backend on_time/off_time formatting).
// Used for aggregated parent rows; leaf machines display the backend string directly.
function formatHoursToStr(hours) {
  const totalMins = Math.round((Number(hours) || 0) * 60);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  if (h > 0 && m > 0) return `${h} hrs ${m} mins`;
  if (h > 0) return `${h} hrs`;
  return `${m} mins`;
}

// Colors for the "By Source" tab segments (Grid / Solar / Genset).
const SOURCE_COLORS = { grid: "#0ea5e9", solar: "#f59e0b", genset: "#64748b" };
const SOURCE_LEGEND = [
  { key: "grid", label: "Grid", color: SOURCE_COLORS.grid, helpId: "historical.sourceGrid" },
  { key: "solar", label: "Solar", color: SOURCE_COLORS.solar, helpId: "historical.sourceSolar" },
  { key: "genset", label: "Genset", color: SOURCE_COLORS.genset, helpId: "historical.sourceGenset" },
];

// /machine-history-summary responses for this session, keyed by user + range +
// branch, so going back to a branch or range already opened is instant, like
// the Historical cards and chart. Only successful responses are kept, so a
// retry after an error still calls the API.
const summaryCache = new Map();

const OnOffChart = ({
  username,
  dateRange,
  branch,
  machinesList,
  scopeMachineNames,
  scopeHierarchyPath,
  currencyMode = "Energy",
  tariffs,
  hierarchyTree: propHierarchyTree,
}) => {
  // ON/OFF stays in hours whatever the toggle says — operating duration has no
  // currency. Only the two energy tabs reprice.
  const isPkr = currencyMode === "PKR";
  const rates = useMemo(() => tariffs || resolveTariffs(null), [tariffs]);
  const energyUnit = unitLabel(isPkr);
  // When a branch is selected in the Historical tab, scopeMachineNames holds the
  // machine names under it; keep only those rows so all three tabs
  // (Peak/Off-Peak, ON/OFF, By Source) follow the segregation. null = no filter.
  // The numbers themselves are already branch-scoped by the server (see branch).
  const applyScope = useCallback(
    (rows) => {
      if (!scopeMachineNames || !scopeMachineNames.length) return rows;
      const set = new Set(scopeMachineNames);
      return rows.filter((r) => set.has(r.machine));
    },
    [scopeMachineNames]
  );

  // Roots for the parent-child tables: when a branch is selected, root the tree
  // at that branch (found by hierarchy_path) so the table shows only its subtree;
  // otherwise render the client root's children (whole facility).
  const getScopedRoots = useCallback(
    (tree) => {
      const asArray = (ch) => (Array.isArray(ch) ? ch : Object.values(ch || {}));
      if (!tree) return [];
      if (scopeHierarchyPath) {
        let found = null;
        const walk = (node) => {
          if (found || !node) return;
          if (node.hierarchy_path === scopeHierarchyPath) {
            found = node;
            return;
          }
          asArray(node.children).forEach(walk);
        };
        walk(tree);
        return found ? [found] : [];
      }
      return tree.children ? asArray(tree.children) : [];
    },
    [scopeHierarchyPath]
  );
  const [activeTab, setActiveTab] = useState("peakOffPeak");
  // One /machine-history-summary payload feeds all three tabs.
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(false);
  // Bumped per request so a slow response for an old range/branch can never
  // overwrite a newer one; this call can take minutes on long ranges.
  const summaryRequestRef = useRef(0);
  const chartRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [localHierarchyTree, setLocalHierarchyTree] = useState(null);
  const hierarchyTree = propHierarchyTree || localHierarchyTree;

  const [expandedRows, setExpandedRows] = useState(new Set());
  const [hoverTip, setHoverTip] = useState(null); // { x, y, row }
  const [legendHover, setLegendHover] = useState(null); // "peak" | "offPeak" | null
  const [sourceHoverTip, setSourceHoverTip] = useState(null); // { x, y, row }
  const [sourceLegendHover, setSourceLegendHover] = useState(null); // "grid" | "solar" | "genset" | null
  const [summaryReloadKey, setSummaryReloadKey] = useState(0); // bump to force a refetch

  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiModalPayload, setAiModalPayload] = useState(null);

  const handleAnalyzeOnOffChart = () => {
    const isSourceTab = activeTab === "bySource" || activeTab === "source";
    const isPeakOffPeakTab = activeTab === "peakOffPeak";
    const isOnOffTab = activeTab === "onOff";

    let title = "Machine Performance Analytics";
    let unit = "Hours";
    let metricName = "Machine Operational Hours";
    let timestamps = ["Active", "Idle"];
    let datasets = [];

    if (isPeakOffPeakTab) {
      title = `Peak / Off-Peak Machine ${isPkr ? "Cost" : "Energy"}`;
      unit = energyUnit;
      metricName = `Peak / Off-Peak ${isPkr ? "Cost" : "Energy"}`;
      timestamps = isPkr
        ? ["Peak Cost", "Off-Peak Cost"]
        : ["Peak Energy", "Off-Peak Energy"];
      datasets = (peakOffPeakValues || []).map((row) => ({
        label: row.machine || row.machine_name || row.name || "Machine",
        data: [
          Number(row.peakUnits ?? row.peak_units ?? 0),
          Number(row.offPeakUnits ?? row.off_peak_units ?? 0),
        ],
      }));
    } else if (isOnOffTab) {
      title = "On / Off Machine Runtimes";
      unit = "Hours";
      metricName = "Machine Operational Hours";
      timestamps = ["ON Hours", "OFF Hours"];
      datasets = (onOffData || []).map((row) => {
        const on = typeof row.onHours === "number" ? row.onHours : parseTimeStrToHours(row.onTimeRaw || row.on_time);
        const off = typeof row.offHours === "number" ? row.offHours : parseTimeStrToHours(row.offTimeRaw || row.off_time);
        return {
          label: row.machine || row.machine_name || row.name || "Machine",
          data: [Number(on.toFixed(2)), Number(off.toFixed(2))],
        };
      });
    } else if (isSourceTab) {
      title = `Machine ${isPkr ? "Cost" : "Energy"} By Source`;
      unit = energyUnit;
      metricName = `${isPkr ? "Cost" : "Energy"} By Source`;
      timestamps = ["Grid", "Solar", "Genset"];
      datasets = (sourceValues || []).map((row) => ({
        label: row.machine || row.name || "Machine",
        data: [
          Number(row.grid || 0),
          Number(row.solar || 0),
          Number(row.genset || 0),
        ],
      }));
    }

    const payload = buildChartPayload({
      chartTitle: title,
      metricName: metricName,
      unit: unit,
      timestamps: timestamps,
      datasets: datasets,
      dateRange: dateRange,
    });
    setAiModalPayload(payload);
    setAiModalOpen(true);
  };

  // Fetch the real hierarchy tree (same source as Live tab) so the
  // Peak/Off-Peak view can render a parent-child table like the Live table.
  useEffect(() => {
    if (propHierarchyTree !== undefined) return; // Skip if parent is controlling this prop
    if (!username) return;
    let cancelled = false;
    api
      .get(`/hierarchy-tree`, { params: { username } })
      .then((res) => {
        if (!cancelled) setLocalHierarchyTree(res.data || null);
      })
      .catch((err) => {
        console.warn("OnOffChart: hierarchy-tree fetch failed", err);
        if (!cancelled) setLocalHierarchyTree(null);
      });
    return () => {
      cancelled = true;
    };
  }, [username, propHierarchyTree]);

  const toggleRow = (name) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        document.fullscreenElement === chartRef.current ||
        document.webkitFullscreenElement === chartRef.current
      );
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Format Date object to YYYY-MM-DD HH:mm:ss
  const formatToSQL = (date) => {
    if (!date) return "";
    const pad = (n) => String(n).padStart(2, "0");
    const d = new Date(date);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  // Keyed on the formatted range, not the dateRange object. A new object with
  // the same start and end used to abort the in-flight call (Chrome: canceled)
  // and start an identical one.
  const summaryRangeKey =
    dateRange?.start && dateRange?.end
      ? `${formatToSQL(dateRange.start)}|${formatToSQL(dateRange.end)}|${branch && branch !== "All" ? branch : "ALL"}`
      : "";

  // /machine-history-summary replaces /machine-onoff, /machine-peak-offpeak and
  // /machine-energy-by-source here: one call, each machine carrying all three
  // blocks, scoped server-side to the selected branch. Re-runs when the user
  // explicitly retries (summaryReloadKey bump).
  useEffect(() => {
    if (!username || !summaryRangeKey) return undefined;
    const [startDatetime, endDatetime, branchParam] = summaryRangeKey.split("|");
    const requestId = ++summaryRequestRef.current;
    let active = true;

    const fetchSummary = async () => {
      setSummaryLoading(true);
      setSummaryError(false);

      try {
        const params = {
          username,
          start_datetime: startDatetime,
          end_datetime: endDatetime,
          branch: branchParam,
        };
        const cacheKey = JSON.stringify(params);
        let body = summaryCache.get(cacheKey);
        if (!body) {
          const response = await api.get(`/machine-history-summary`, {
            params,
            // Do not pass an AbortSignal. Cleanup used to abort this call, and
            // Chrome listed it as (canceled) even though the range was unchanged.
            timeout: 0,
          });
          body = response.data;
          if (body && body.status === "success" && body.data) {
            summaryCache.set(cacheKey, body);
          }
        }
        if (!active || requestId !== summaryRequestRef.current) return;
        setSummary(body && body.status === "success" && body.data ? body : null);
      } catch (err) {
        if (!active || requestId !== summaryRequestRef.current) return;
        console.error("Error fetching machine history summary:", err);
        setSummary(null);
        setSummaryError(true);
      } finally {
        if (active && requestId === summaryRequestRef.current) setSummaryLoading(false);
      }
    };

    fetchSummary();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, summaryRangeKey, summaryReloadKey]);

  // Each tab reads its own block. A block is null where it doesn't apply (onoff
  // on a solar inverter, energy_by_source on a grid meter), so that machine is
  // left out of that tab only.
  const summaryData = summary?.data;
  const totalApiHours = summary?.total_hours || 0;

  const onOffData = useMemo(() => {
    if (!summaryData) return [];
    const parsed = Object.keys(summaryData)
      .filter((machine) => summaryData[machine]?.onoff)
      .map((machine) => {
        const info = summaryData[machine].onoff;
        let onHours = parseTimeStrToHours(info.on_time);
        let offHours = parseTimeStrToHours(info.off_time);
        let offTimeRaw = info.off_time;

        if (onHours + offHours < totalApiHours) {
          offHours = Number((totalApiHours - onHours).toFixed(2));
          // Let user know the tooltip value was adjusted to meet the total_hours
          offTimeRaw = `${offHours} hrs`;
        }

        return {
          machine,
          onHours,
          offHours,
          onTimeRaw: info.on_time,
          offTimeRaw,
          onoff: info.onoff,
          offon: info.offon,
        };
      });
    // Sort the machines based on maximum on hours (descending)
    parsed.sort((a, b) => b.onHours - a.onHours);
    return applyScope(parsed);
  }, [summaryData, totalApiHours, applyScope]);

  const peakOffPeakData = useMemo(() => {
    if (!summaryData) return [];
    const parsed = Object.keys(summaryData)
      .filter((machine) => summaryData[machine]?.peak_offpeak)
      .map((machine) => {
        const info = summaryData[machine].peak_offpeak;
        const peakUnits = Number(info.peak_units || 0);
        const offPeakUnits = Number(info.off_peak_units || 0);
        return {
          machine,
          peakUnits: Number(peakUnits.toFixed(2)),
          offPeakUnits: Number(offPeakUnits.toFixed(2)),
        };
      });

    parsed.sort(
      (a, b) =>
        b.peakUnits + b.offPeakUnits - (a.peakUnits + a.offPeakUnits),
    );
    return applyScope(parsed);
  }, [summaryData, applyScope]);

  const sourceData = useMemo(() => {
    if (!summaryData) return [];
    const parsed = Object.keys(summaryData)
      .map((machine) => {
        const item = summaryData[machine];
        if (!item) return null;
        let info = item.energy_by_source;
        if (!info && item.peak_offpeak) {
          const cat = (item.category || "").toUpperCase();
          const peakUnits = Number(item.peak_offpeak.peak_units || 0);
          const offPeakUnits = Number(item.peak_offpeak.off_peak_units || 0);
          const totalUnits = Number((peakUnits + offPeakUnits).toFixed(2));
          if (totalUnits > 0) {
            if (cat === "GENERATOR") {
              info = { grid_units: 0, solar_units: 0, genset_units: totalUnits, total_units: totalUnits, grid_pct: 0, solar_pct: 0, genset_pct: 100 };
            } else if (cat === "SOLAR") {
              info = { grid_units: 0, solar_units: totalUnits, genset_units: 0, total_units: totalUnits, grid_pct: 0, solar_pct: 100, genset_pct: 0 };
            } else {
              info = { grid_units: totalUnits, solar_units: 0, genset_units: 0, total_units: totalUnits, grid_pct: 100, solar_pct: 0, genset_pct: 0 };
            }
          }
        }
        if (!info) return null;
        return {
          machine,
          grid: Number(info.grid_units || 0),
          solar: Number(info.solar_units || 0),
          genset: Number(info.genset_units || 0),
          total: Number(info.total_units || 0),
          gridPct: Number(info.grid_pct || 0),
          solarPct: Number(info.solar_pct || 0),
          gensetPct: Number(info.genset_pct || 0),
        };
      })
      .filter((d) => d && d.total > 0);

    parsed.sort((a, b) => b.total - a.total);
    return applyScope(parsed);
  }, [summaryData, applyScope]);

  // The Historical tab fires many heavy requests at once; against a flaky DB a
  // single attempt can fail and, without a retry, the error would stick. When
  // the user opens the By Source tab and it's in an error state, retry once
  // (the load has usually settled by then). Depends only on the tab flag so a
  // repeatedly-failing retry can't loop.
  const activeTabIsSource = activeTab === "bySource";
  useEffect(() => {
    if (activeTabIsSource && summaryError && !summaryLoading) {
      setSummaryReloadKey((k) => k + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabIsSource]);

  // Tooltips/legend highlights are tab-specific. Clear them when the tab
  // changes so a tooltip opened on one tab (e.g. ON/OFF "Hours ON/OFF") does
  // not linger over another tab's table.
  useEffect(() => {
    setHoverTip(null);
    setSourceHoverTip(null);
    setLegendHover(null);
    setSourceLegendHover(null);
  }, [activeTab]);

  const isPeakOffPeakTab = activeTab === "peakOffPeak";
  const isOnOffTab = activeTab === "onOff";
  const isSourceTab = activeTab === "bySource";

  // Peak and off-peak hours bill at different rates, so each segment is priced
  // on its own. That deliberately shifts the 100% split: a machine that is 50/50
  // on kWh is majority-peak on cost.
  const peakOffPeakValues = useMemo(() => {
    if (!isPkr) return peakOffPeakData;
    return (peakOffPeakData || []).map((row) => ({
      ...row,
      peakUnits: (row.peakUnits || 0) * rates.peak,
      offPeakUnits: (row.offPeakUnits || 0) * rates.offPeak,
    }));
  }, [peakOffPeakData, isPkr, rates]);

  // Same idea across sources: grid, solar and genset units each carry their own
  // tariff, so the cost split is not the energy split.
  const sourceValues = useMemo(() => {
    if (!isPkr) return sourceData;
    return (sourceData || []).map((row) => {
      const grid = (row.grid || 0) * rates.grid;
      const solar = (row.solar || 0) * rates.solar;
      const genset = (row.genset || 0) * rates.genset;
      const total = grid + solar + genset;
      return {
        ...row,
        grid,
        solar,
        genset,
        total,
        gridPct: total > 0 ? (grid / total) * 100 : 0,
        solarPct: total > 0 ? (solar / total) * 100 : 0,
        gensetPct: total > 0 ? (genset / total) * 100 : 0,
      };
    });
  }, [sourceData, isPkr, rates]);

  const activeData = useMemo(() => {
    return isPeakOffPeakTab
      ? peakOffPeakValues
      : isOnOffTab
        ? onOffData
        : [];
  }, [isPeakOffPeakTab, peakOffPeakValues, isOnOffTab, onOffData]);
  const activeLoading = summaryLoading;
  const activeError = !summaryError
    ? null
    : isPeakOffPeakTab
      ? "Failed to fetch machine Peak/Off-Peak data"
      : isOnOffTab
        ? "Failed to fetch machine ON/OFF data"
        : "Failed to fetch energy-by-source data";

  const displayData = useMemo(() => {
    if (!activeData || activeData.length === 0) return [];

    // Prioritized sorting: GRID (0) > SOLAR (1) > GENERATOR (2) > MACHINE (3) > Others (4)
    const sorted = [...activeData].sort((a, b) => {
      const getPriority = (name) => {
        const m = (machinesList || []).find((item) => item.name === name);
        const cat = (m?.category || "").toUpperCase();
        if (cat === "GRID") return 0;
        if (cat === "SOLAR") return 1;
        if (cat === "GENERATOR") return 2;
        if (cat === "MACHINE") return 3;
        return 4;
      };

      const pA = getPriority(a.machine);
      const pB = getPriority(b.machine);

      if (pA !== pB) return pA - pB;

      if (isPeakOffPeakTab) {
        const valA = (a.peakUnits || 0) + (a.offPeakUnits || 0);
        const valB = (b.peakUnits || 0) + (b.offPeakUnits || 0);
        return valB - valA;
      } else {
        return (b.onHours || 0) - (a.onHours || 0);
      }
    });

    if (!isFullscreen && sorted.length > 10) {
      return sorted.slice(0, 10);
    }
    return sorted;
  }, [activeData, isFullscreen, machinesList, isPeakOffPeakTab]);

  // ===== "By Source" tab data (energy split Grid / Solar / Genset per machine) =====
  const sourceDisplayData = useMemo(() => {
    if (!sourceValues || sourceValues.length === 0) return [];
    const sorted = [...sourceValues].sort((a, b) => b.total - a.total);
    if (!isFullscreen && sorted.length > 10) {
      return sorted.slice(0, 10);
    }
    return sorted;
  }, [sourceValues, isFullscreen]);



  // machine_name -> { grid, solar, genset } for the By Source hierarchy table.
  const sourceDataMap = useMemo(() => {
    const m = new Map();
    (sourceValues || []).forEach((d) => {
      m.set(d.machine, { grid: d.grid || 0, solar: d.solar || 0, genset: d.genset || 0 });
    });
    return m;
  }, [sourceValues]);

  // Same hierarchy tree the Peak/Off-Peak and ON/OFF tabs use, but each node
  // carries its Grid/Solar/Genset split. A node shows its own metered value if
  // present, else the sum of its subtree.
  const sourceTableRows = useMemo(() => {
    if (!hierarchyTree) return [];

    const getKids = (node) => {
      const ch = node?.children;
      if (!ch) return [];
      return Array.isArray(ch) ? ch : Object.values(ch);
    };

    const computeAgg = (node) => {
      const own = sourceDataMap.get(node.machine_name);
      if (own) return { grid: own.grid, solar: own.solar, genset: own.genset };
      let grid = 0;
      let solar = 0;
      let genset = 0;
      getKids(node).forEach((c) => {
        const x = computeAgg(c);
        grid += x.grid;
        solar += x.solar;
        genset += x.genset;
      });
      return { grid, solar, genset };
    };

    const rows = [];
    const walk = (node, depth) => {
      const kids = getKids(node);
      const name = node.machine_name;
      if (name) {
        const agg = computeAgg(node);
        rows.push({
          name,
          depth,
          category: (node.category || "").toUpperCase(),
          hasChildren: kids.length > 0,
          grid: agg.grid,
          solar: agg.solar,
          genset: agg.genset,
          total: agg.grid + agg.solar + agg.genset,
        });
      }
      if (kids.length > 0 && (!name || expandedRows.has(name))) {
        kids.forEach((c) => walk(c, depth + 1));
      }
    };

    const roots = getScopedRoots(hierarchyTree);
    roots.forEach((r) => walk(r, 0));
    return rows;
  }, [hierarchyTree, sourceDataMap, expandedRows, getScopedRoots]);



  // ===== Hierarchy table (works for both Peak/Off-Peak and ON/OFF tabs) =====
  // Tab-specific colors / labels for the two stacked segments (A + B).
  const tableConfig = isPeakOffPeakTab
    ? { aColor: "#e11d48", bColor: "#1e3a4e", aLabel: "Peak", bLabel: "Off-Peak", aHelp: "historical.peakEnergy", bHelp: "historical.offPeakEnergy", unit: energyUnit, header: `${isPkr ? "Cost" : "Energy"} [${energyUnit}]` }
    : { aColor: "#4caf50", bColor: "#f44336", aLabel: "Hours ON", bLabel: "Hours OFF", aHelp: "historical.hoursOn", bHelp: "historical.hoursOff", unit: "hrs", header: "Hours" };

  const tableDataMap = useMemo(() => {
    const m = new Map();
    if (isPeakOffPeakTab) {
      (peakOffPeakValues || []).forEach((d) => {
        m.set(d.machine, {
          a: d.peakUnits || 0,
          b: d.offPeakUnits || 0,
          aStr: `${formatAmount(d.peakUnits || 0)} ${energyUnit}`,
          bStr: `${formatAmount(d.offPeakUnits || 0)} ${energyUnit}`,
          onoff: null,
          offon: null,
        });
      });
    } else {
      (onOffData || []).forEach((d) => {
        m.set(d.machine, {
          a: d.onHours || 0,
          b: d.offHours || 0,
          // Prefer the backend's pre-formatted string ("13 hrs 40 mins"); fall back to formatting.
          aStr: d.onTimeRaw || formatHoursToStr(d.onHours),
          bStr: d.offTimeRaw || formatHoursToStr(d.offHours),
          onoff: d.onoff ?? 0,
          offon: d.offon ?? 0,
        });
      });
    }
    return m;
  }, [isPeakOffPeakTab, peakOffPeakValues, onOffData, energyUnit]);

  // Flattened, expand-aware rows built from the hierarchy tree.
  // A node shows its own metered value if present, else the sum of its subtree.
  const tableRows = useMemo(() => {
    if (!hierarchyTree) return [];

    const getKids = (node) => {
      const ch = node?.children;
      if (!ch) return [];
      return Array.isArray(ch) ? ch : Object.values(ch);
    };

    const computeAgg = (node) => {
      const own = tableDataMap.get(node.machine_name);
      if (own) {
        return {
          a: own.a,
          b: own.b,
          avgA: own.a,
          avgB: own.b,
          aStr: own.aStr,
          bStr: own.bStr,
          onoff: own.onoff || 0,
          offon: own.offon || 0,
          count: 1,
        };
      }
      let a = 0;
      let b = 0;
      let onoff = 0;
      let offon = 0;
      let count = 0;
      getKids(node).forEach((c) => {
        const x = computeAgg(c);
        a += x.a;
        b += x.b;
        onoff += x.onoff;
        offon += x.offon;
        count += x.count || 0;
      });
      const avgA = count > 0 ? a / count : 0;
      const avgB = count > 0 ? b / count : 0;
      return {
        a,
        b,
        avgA,
        avgB,
        aStr: isPeakOffPeakTab ? `${formatAmount(a)} ${energyUnit}` : formatHoursToStr(avgA),
        bStr: isPeakOffPeakTab ? `${formatAmount(b)} ${energyUnit}` : formatHoursToStr(avgB),
        onoff,
        offon,
        count,
      };
    };

    const rows = [];
    const walk = (node, depth) => {
      const kids = getKids(node);
      const name = node.machine_name;
      if (name) {
        const agg = computeAgg(node);
        const isVirtual = !tableDataMap.has(name);
        const valA = isPeakOffPeakTab ? agg.a : (isVirtual ? agg.avgA : agg.a);
        const valB = isPeakOffPeakTab ? agg.b : (isVirtual ? agg.avgB : agg.b);
        rows.push({
          name,
          depth,
          category: (node.category || "").toUpperCase(),
          hasChildren: kids.length > 0,
          valA,
          valB,
          valAStr: agg.aStr,
          valBStr: agg.bStr,
          onoff: agg.onoff,
          offon: agg.offon,
          total: valA + valB,
          leafCount: agg.count,
        });
      }
      if (kids.length > 0 && (!name || expandedRows.has(name))) {
        kids.forEach((c) => walk(c, depth + 1));
      }
    };

    // Root at the selected branch when scoped, else the client root's children.
    const roots = getScopedRoots(hierarchyTree);
    roots.forEach((r) => walk(r, 0));
    return rows;
  }, [hierarchyTree, tableDataMap, expandedRows, getScopedRoots, isPeakOffPeakTab, energyUnit]);



  // Default: start with all nodes collapsed so only top nodes are shown.
  useEffect(() => {
    if (!hierarchyTree) return;
    setExpandedRows(new Set());
  }, [hierarchyTree]);

  const series = [
    ...(isPeakOffPeakTab
      ? [
        {
          name: "Peak",
          data: displayData.map((d) => d.peakUnits),
        },
        {
          name: "Off-Peak",
          data: displayData.map((d) => d.offPeakUnits),
        },
      ]
      : [
        {
          name: "Hours ON",
          data: displayData.map((d) => d.onHours),
        },
        {
          name: "Hours OFF",
          data: displayData.map((d) => d.offHours),
        },
      ]),
  ];

  const options = {
    chart: {
      type: "bar",
      stacked: true,
      stackType: "100%",
      toolbar: {
        show: false,
        tools: {
          download: false,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
    },
    colors: isPeakOffPeakTab ? ["#e11d48", "#1e3a4e"] : ["#4caf50", "#f44336"],
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "75%",
      },
    },
    grid: {
      padding: {
        left: 0,
        right: 0,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val > 6 ? Math.round(val) + "%" : "";
      },
      style: {
        fontSize: "11px",
        fontWeight: "bold",
        colors: ["#fff"],
      },
    },
    xaxis: {
      categories: displayData.map((d) => d.machine),
      labels: {
        formatter: function (val) {
          return val + "%";
        },
      },
      title: {
        text: "Percentage (%)",
        style: {
          fontWeight: 600,
          color: "#475569",
        },
      },
    },
    yaxis: {
      title: {
        text: "",
        style: {
          fontWeight: 600,
          color: "#475569",
        },
      },
      labels: {
        minWidth: 105,
        maxWidth: 105,
        style: {
          fontSize: "12px",
          fontFamily: "inherit",
          colors: "#64748b",
        },
      },
    },
    legend: {
      position: "bottom",
      markers: {
        radius: 12,
      },
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const item = displayData[dataPointIndex];
        if (!item) return "";

        if (isPeakOffPeakTab) {
          const total = item.peakUnits + item.offPeakUnits;
          const peakPct = total > 0 ? Math.round((item.peakUnits / total) * 100) : 0;
          const offPct = total > 0 ? Math.round((item.offPeakUnits / total) * 100) : 0;
          return `
            <div style="padding: 12px; font-family: inherit; font-size: 13px; min-width: 170px;">
              <div style="font-weight: 700; margin-bottom: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px;">
                ${item.machine}
              </div>
              <div style="color: #be123c; font-weight: 600; margin-bottom: 4px;">
                <span style="display:inline-block; width: 10px; height: 10px; background-color: #e11d48; border-radius: 50%; margin-right: 6px;"></span>
                Peak: ${formatAmount(item.peakUnits)} ${energyUnit} (${peakPct}%)
              </div>
              <div style="color: #1e3a4e; font-weight: 600; margin-bottom: 6px;">
                <span style="display:inline-block; width: 10px; height: 10px; background-color: #1e3a4e; border-radius: 50%; margin-right: 6px;"></span>
                Off-Peak: ${formatAmount(item.offPeakUnits)} ${energyUnit} (${offPct}%)
              </div>
              <div style="margin-top: 6px; padding-top: 6px; border-top: 1px dashed #e2e8f0; font-size: 12px; color: #0f172a; font-weight: 700; display: flex; justify-content: space-between;">
                <span>Total Energy:</span>
                <span>${formatAmount(total)} ${energyUnit}</span>
              </div>
              ${totalApiHours ? `
              <div style="margin-top: 3px; font-size: 11px; color: #64748b; display: flex; justify-content: space-between;">
                <span>Total Time:</span>
                <span>${totalApiHours} hrs</span>
              </div>` : ''}
            </div>
          `;
        }

        const total = item.onHours + item.offHours;
        const onPct = total > 0 ? Math.round((item.onHours / total) * 100) : 0;
        const offPct = total > 0 ? Math.round((item.offHours / total) * 100) : 0;

        return `
          <div style="padding: 12px; font-family: inherit; font-size: 13px; min-width: 150px;">
            <div style="font-weight: 700; margin-bottom: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px;">
              ${item.machine}
            </div>
            <div style="color: #15803d; font-weight: 600; margin-bottom: 4px;">
              <span style="display:inline-block; width: 10px; height: 10px; background-color: #4caf50; border-radius: 50%; margin-right: 6px;"></span>
              Hours ON: ${item.onTimeRaw} (${onPct}%)
            </div>
            <div style="color: #b91c1c; font-weight: 600; margin-bottom: 8px;">
              <span style="display:inline-block; width: 10px; height: 10px; background-color: #f44336; border-radius: 50%; margin-right: 6px;"></span>
              Hours OFF: ${item.offTimeRaw} (${offPct}%)
            </div>
            <div style="background: #f8fafc; padding: 6px; border-radius: 4px; border: 1px solid #e2e8f0;">
              <div style="color: #475569; font-size: 12px; font-weight: 600; margin-bottom: 4px;">Machine State</div>
              <div style="color: #475569; font-size: 12px;">• ${item.machine} ON to OFF: ${item.onoff} times</div>
              <div style="color: #475569; font-size: 12px;">• ${item.machine} OFF to ON: ${item.offon} times</div>
            </div>
          </div>
        `;
      },
    },
  };

  const chartDataExport = {
    labels: displayData.map((d) => d.machine),
    datasets: isPeakOffPeakTab
      ? [
        { label: "Peak", data: displayData.map((d) => d.peakUnits) },
        { label: "Off-Peak", data: displayData.map((d) => d.offPeakUnits) },
      ]
      : [
        { label: "Hours ON", data: displayData.map((d) => d.onHours) },
        { label: "Hours OFF", data: displayData.map((d) => d.offHours) },
      ],
    unit: isPeakOffPeakTab ? energyUnit : "hrs",
  };

  const sourceChartDataExport = {
    labels: sourceDisplayData.map((d) => d.machine),
    datasets: [
      { label: "Grid", data: sourceDisplayData.map((d) => d.grid) },
      { label: "Solar", data: sourceDisplayData.map((d) => d.solar) },
      { label: "Genset", data: sourceDisplayData.map((d) => d.genset) },
    ],
    unit: energyUnit,
  };

  return (
    <div
      ref={chartRef}
      style={{
        backgroundColor: "#fff",
        borderRadius: "16px",
        border: "1px solid #e2e8f0",
        overflow: "visible",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        display: isFullscreen ? "flex" : "block",
        flexDirection: "column",
        width: isFullscreen ? "100%" : "auto",
        height: isFullscreen ? "100%" : "auto"
      }}
    >
      {hoverTip && (
        <div
          style={{
            position: "fixed",
            // Flip to the other side of the cursor near the right/bottom edge so the box
            // never runs off-screen (transform shifts by the box's own size, any height).
            left: hoverTip.x + (hoverTip.x + 240 > window.innerWidth ? -14 : 14),
            top: hoverTip.y + (hoverTip.y + 190 > window.innerHeight ? -14 : 14),
            transform: `translate(${hoverTip.x + 240 > window.innerWidth ? "-100%" : "0"}, ${hoverTip.y + 190 > window.innerHeight ? "-100%" : "0"})`,
            zIndex: 2000,
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            boxShadow: "0 6px 18px rgba(15,23,42,0.15)",
            padding: 12,
            fontSize: 13,
            minWidth: 160,
            pointerEvents: "none",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8, borderBottom: "1px solid #e2e8f0", paddingBottom: 6 }}>
            {hoverTip.row.name} {hoverTip.row.hasChildren && !isPeakOffPeakTab ? "(Average)" : ""}
          </div>
          <div style={{ color: tableConfig.aColor, fontWeight: 600, marginBottom: 4 }}>
            <span style={{ display: "inline-block", width: 10, height: 10, background: tableConfig.aColor, borderRadius: "50%", marginRight: 6 }} />
            {hoverTip.row.hasChildren && !isPeakOffPeakTab ? "Avg " : ""}{tableConfig.aLabel}: {hoverTip.row.valAStr} {hoverTip.row.total > 0 ? `(${((hoverTip.row.valA / hoverTip.row.total) * 100).toFixed(1)}%)` : ""}
          </div>
          <div style={{ color: tableConfig.bColor, fontWeight: 600 }}>
            <span style={{ display: "inline-block", width: 10, height: 10, background: tableConfig.bColor, borderRadius: "50%", marginRight: 6 }} />
            {hoverTip.row.hasChildren && !isPeakOffPeakTab ? "Avg " : ""}{tableConfig.bLabel}: {hoverTip.row.valBStr} {hoverTip.row.total > 0 ? `(${((hoverTip.row.valB / hoverTip.row.total) * 100).toFixed(1)}%)` : ""}
          </div>
          {isPeakOffPeakTab ? (
            <div style={{ marginTop: 8, paddingTop: 6, borderTop: "1px solid #e2e8f0", fontSize: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", color: "#0f172a", fontWeight: 700 }}>
                <span>Total Energy:</span>
                <span>{formatAmount(hoverTip.row.total)} {tableConfig.unit}</span>
              </div>
              {totalApiHours ? (
                <div style={{ display: "flex", justifyContent: "space-between", color: "#64748b", marginTop: 3 }}>
                  <span>Total Time:</span>
                  <span>{totalApiHours} hrs</span>
                </div>
              ) : null}
            </div>
          ) : (
            <div style={{ marginTop: 8, paddingTop: 6, borderTop: "1px solid #e2e8f0", color: "#475569", fontSize: 12 }}>
              {hoverTip.row.hasChildren && (
                <div style={{ marginBottom: 4, color: "#64748b" }}>
                  Averaged over {hoverTip.row.leafCount} {hoverTip.row.leafCount === 1 ? "machine" : "machines"}
                </div>
              )}
              <div>OFF → ON: {hoverTip.row.offon ?? 0} times</div>
              <div>ON → OFF: {hoverTip.row.onoff ?? 0} times</div>
            </div>
          )}
        </div>
      )}
      {sourceHoverTip && (
        <div
          style={{
            position: "fixed",
            left: sourceHoverTip.x + 14,
            top: sourceHoverTip.y + 14,
            zIndex: 2000,
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            boxShadow: "0 6px 18px rgba(15,23,42,0.15)",
            padding: 12,
            fontSize: 13,
            minWidth: 200,
            pointerEvents: "none",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8, borderBottom: "1px solid #e2e8f0", paddingBottom: 6 }}>
            {sourceHoverTip.row.name || sourceHoverTip.row.machine}
          </div>
          {[
            { label: "Grid", color: SOURCE_COLORS.grid, val: sourceHoverTip.row.grid },
            { label: "Solar", color: SOURCE_COLORS.solar, val: sourceHoverTip.row.solar },
            { label: "Genset", color: SOURCE_COLORS.genset, val: sourceHoverTip.row.genset },
          ].map((s) => {
            const totalVal = sourceHoverTip.row.total || 0;
            const pct = totalVal > 0 ? ((s.val / totalVal) * 100).toFixed(1) : "0.0";
            return (
              <div
                key={s.label}
                style={{ color: s.color, fontWeight: 600, marginBottom: 4, display: "flex", justifyContent: "space-between", gap: 12 }}
              >
                <span>
                  <span style={{ display: "inline-block", width: 10, height: 10, background: s.color, borderRadius: "50%", marginRight: 6 }} />
                  {s.label}
                </span>
                <span>{formatAmount(s.val)} {energyUnit} ({pct}%)</span>
              </div>
            );
          })}
          <div style={{ borderTop: "1px solid #e2e8f0", marginTop: 4, paddingTop: 6, display: "flex", justifyContent: "space-between", fontWeight: 700, color: "#0f172a" }}>
            <span>Total</span>
            <span>{formatAmount(sourceHoverTip.row.total)} {energyUnit}</span>
          </div>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid #f1f5f9" }}>
        <div style={{ display: "flex", flexDirection: "row", gap: "8px", alignItems: "center" }}>
          <button
            onClick={() => setActiveTab("peakOffPeak")}
            style={{
              border: "none",
              borderRadius: "6px",
              padding: "8px 14px",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
              background: isPeakOffPeakTab ? "#0ea5e9" : "transparent",
              color: isPeakOffPeakTab ? "#fff" : "#0369a1",
            }}
          >
            <FieldHelp id="historical.peakOffPeakTab">Peak/ Off-Peak</FieldHelp>
          </button>
          <button
            onClick={() => setActiveTab("onOff")}
            style={{
              border: "none",
              borderRadius: "6px",
              padding: "8px 14px",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
              background: isOnOffTab ? "#0ea5e9" : "transparent",
              color: isOnOffTab ? "#fff" : "#0369a1",
            }}
          >
            <FieldHelp id="historical.onOffTab">ON/OFF</FieldHelp>
          </button>
          <button
            onClick={() => setActiveTab("bySource")}
            style={{
              border: "none",
              borderRadius: "6px",
              padding: "8px 14px",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
              background: isSourceTab ? "#0ea5e9" : "transparent",
              color: isSourceTab ? "#fff" : "#0369a1",
            }}
          >
            <FieldHelp id="historical.bySourceTab">By Source</FieldHelp>
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <AIAnalyzerButton onClick={handleAnalyzeOnOffChart} />
          <ChartBurgerMenu
            chartRef={chartRef}
            chartTitle={isPeakOffPeakTab ? (isPkr ? "Peak_OffPeak_Cost_Chart" : "Peak_OffPeak_Chart") : isOnOffTab ? "On_Off_Chart" : (isPkr ? "Cost_By_Source_Chart" : "Energy_By_Source_Chart")}
            onAIAnalyze={handleAnalyzeOnOffChart}
            chartData={
              isSourceTab
                ? (sourceDisplayData.length > 0 ? sourceChartDataExport : null)
                : (displayData.length > 0 ? chartDataExport : null)
            }
            exportContainer={true}
            chartType="onoff"
          />
        </div>
      </div>
      {activeLoading ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
          {isPeakOffPeakTab
            ? "Loading Peak/Off-Peak chart data..."
            : isSourceTab
              ? `Loading ${isPkr ? "cost" : "energy"}-by-source data...`
              : "Loading ON/OFF chart data..."}
        </div>
      ) : activeError ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#ef4444" }}>
          <div>{activeError}</div>
          {isSourceTab && (
            <button
              type="button"
              onClick={() => setSummaryReloadKey((k) => k + 1)}
              style={{
                marginTop: 14,
                border: "1px solid #0ea5e9",
                borderRadius: 6,
                padding: "8px 18px",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                background: "#0ea5e9",
                color: "#fff",
              }}
            >
              Retry
            </button>
          )}
        </div>
      ) : isSourceTab ? (
        sourceValues.length === 0 ? (
          <div key="source-empty" style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
            No energy-by-source data available for the selected period.
          </div>
        ) : sourceTableRows.length > 0 ? (
          <div key="source-view" style={{ padding: "8px 16px 20px", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", maxHeight: isFullscreen ? "none" : 450 }}>
              <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: 13 }}>
                <thead style={{ display: "none" }}>
                  <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                    <th style={{ textAlign: "left", padding: "10px 12px", color: "#475569", fontWeight: 700, position: "sticky", top: 0, background: "#fff", width: "180px" }}>Machine</th>
                    <th style={{ textAlign: "left", padding: "10px 12px", color: "#475569", fontWeight: 700, position: "sticky", top: 0, background: "#fff" }}>Energy by Source</th>
                  </tr>
                </thead>
                <tbody>
                  {sourceTableRows.map((row) => (
                    <tr
                      key={`${row.name}-${row.depth}`}
                      style={{ borderBottom: "1px solid #f1f5f9", background: row.hasChildren ? "#f8fafc" : "#fff" }}
                    >
                      <td style={{ padding: "8px 12px", width: "180px", maxWidth: "180px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        <div style={{ display: "flex", alignItems: "center", paddingLeft: row.depth * 18, overflow: "hidden" }}>
                          {row.hasChildren ? (
                            <button
                              type="button"
                              onClick={() => toggleRow(row.name)}
                              style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, marginRight: 6, width: 16, color: "#64748b", fontSize: 11, flexShrink: 0 }}
                              aria-label={expandedRows.has(row.name) ? "Collapse" : "Expand"}
                            >
                              {expandedRows.has(row.name) ? "▼" : "▶"}
                            </button>
                          ) : (
                            <span style={{ display: "inline-block", width: 16, marginRight: 6, flexShrink: 0 }} />
                          )}
                          <span style={{ fontWeight: row.hasChildren ? 700 : 500, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.name}>
                            {row.name}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: "8px 12px", width: "100%" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                          <div
                            style={{ height: 26, background: "#f1f5f9", borderRadius: 6, overflow: "hidden", display: "flex", cursor: "pointer", flex: 1 }}
                            onMouseEnter={(e) => setSourceHoverTip({ x: e.clientX, y: e.clientY, row })}
                            onMouseMove={(e) => setSourceHoverTip({ x: e.clientX, y: e.clientY, row })}
                            onMouseLeave={() => setSourceHoverTip(null)}
                          >
                            {row.total > 0 && (
                              <div style={{ width: "100%", height: "100%", display: "flex" }}>
                                {SOURCE_LEGEND.map((s) => {
                                  const val = row[s.key] || 0;
                                  const pct = (val / row.total) * 100;
                                  return (
                                    <div
                                      key={s.key}
                                      style={{
                                        width: `${pct}%`,
                                        background: s.color,
                                        opacity: sourceLegendHover && sourceLegendHover !== s.key ? 0.2 : 1,
                                        transition: "opacity 0.15s",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#fff",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                        textShadow: "0 1px 2px rgba(0,0,0,0.35)",
                                        userSelect: "none",
                                      }}
                                    >
                                      {pct >= 6 ? `${Math.round(pct)}%` : ""}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#334155",
                              minWidth: 85,
                              textAlign: "right",
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                            }}
                            title={`Total ${isPkr ? "Cost" : "Energy"}: ${formatAmount(row.total)} ${energyUnit}`}
                          >
                            {formatAmount(row.total)} {energyUnit}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 20, padding: "12px 0 0", fontSize: 12, color: "#475569", flexWrap: "wrap" }}>
              {SOURCE_LEGEND.map((s) => (
                <span
                  key={s.key}
                  onMouseEnter={() => setSourceLegendHover(s.key)}
                  onMouseLeave={() => setSourceLegendHover(null)}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", opacity: sourceLegendHover && sourceLegendHover !== s.key ? 0.4 : 1, transition: "opacity 0.15s" }}
                >
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: s.color, display: "inline-block" }} />
                  <FieldHelp id={s.helpId}>{s.label}</FieldHelp>
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div key="source-view-flat" style={{ padding: "8px 16px 20px", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", maxHeight: isFullscreen ? "none" : 450 }}>
              <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: 13 }}>
                <thead style={{ display: "none" }}>
                  <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                    <th style={{ textAlign: "left", padding: "10px 12px", color: "#475569", fontWeight: 700, position: "sticky", top: 0, background: "#fff", width: "180px" }}>Machine</th>
                    <th style={{ textAlign: "left", padding: "10px 12px", color: "#475569", fontWeight: 700, position: "sticky", top: 0, background: "#fff" }}>Energy by Source</th>
                  </tr>
                </thead>
                <tbody>
                  {sourceDisplayData.map((row) => (
                    <tr key={row.machine} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "8px 12px", width: "180px", maxWidth: "180px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: 500, color: "#0f172a" }} title={row.machine}>
                        {row.machine}
                      </td>
                      <td style={{ padding: "8px 12px", width: "100%" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                          <div
                            style={{
                              height: 26,
                              background: "#f1f5f9",
                              borderRadius: 6,
                              overflow: "hidden",
                              display: "flex",
                              cursor: "pointer",
                              flex: 1,
                            }}
                            onMouseEnter={(e) => setSourceHoverTip({ x: e.clientX, y: e.clientY, row })}
                            onMouseMove={(e) => setSourceHoverTip({ x: e.clientX, y: e.clientY, row })}
                            onMouseLeave={() => setSourceHoverTip(null)}
                          >
                            {row.total > 0 && (
                              <div style={{ width: "100%", height: "100%", display: "flex" }}>
                                {SOURCE_LEGEND.map((s) => {
                                  const val = row[s.key] || 0;
                                  const pct = (val / row.total) * 100;
                                  return (
                                    <div
                                      key={s.key}
                                      style={{
                                        width: `${pct}%`,
                                        background: s.color,
                                        opacity: sourceLegendHover && sourceLegendHover !== s.key ? 0.2 : 1,
                                        transition: "opacity 0.15s",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#fff",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                        textShadow: "0 1px 2px rgba(0,0,0,0.35)",
                                        userSelect: "none",
                                      }}
                                    >
                                      {pct >= 6 ? `${Math.round(pct)}%` : ""}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#334155",
                              minWidth: 85,
                              textAlign: "right",
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                            }}
                            title={`Total ${isPkr ? "Cost" : "Energy"}: ${formatAmount(row.total)} ${energyUnit}`}
                          >
                            {formatAmount(row.total)} {energyUnit}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 20, padding: "12px 0 0", fontSize: 12, color: "#475569", flexWrap: "wrap" }}>
              {SOURCE_LEGEND.map((s) => (
                <span
                  key={s.key}
                  onMouseEnter={() => setSourceLegendHover(s.key)}
                  onMouseLeave={() => setSourceLegendHover(null)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                    opacity: sourceLegendHover && sourceLegendHover !== s.key ? 0.4 : 1,
                    transition: "opacity 0.15s",
                  }}
                >
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: s.color, display: "inline-block" }} />
                  <FieldHelp id={s.helpId}>{s.label}</FieldHelp>
                </span>
              ))}
            </div>
            {!isFullscreen && sourceValues.length > 10 && (
              <div style={{
                textAlign: "center",
                padding: "10px",
                fontSize: "12px",
                color: "#64748b",
                background: "#f8fafc",
                borderTop: "1px solid #f1f5f9",
                borderRadius: "0 0 16px 16px",
                fontWeight: 500,
              }}>
                Showing top 10 machines. <span style={{ color: "#0ea5e9", fontWeight: 700 }}>Open full screen</span> to view all {sourceValues.length} machines.
              </div>
            )}
          </div>
        )
      ) : tableRows.length > 0 ? (
        <div key="onoff-view" style={{ padding: "8px 16px 20px", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", maxHeight: isFullscreen ? "none" : 450 }}>
            <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: 13 }}>
              <thead style={{ display: "none" }}>
                <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{ textAlign: "left", padding: "10px 12px", color: "#475569", fontWeight: 700, position: "sticky", top: 0, background: "#fff", width: "180px" }}>Machine</th>
                  <th style={{ textAlign: "left", padding: "10px 12px", color: "#475569", fontWeight: 700, position: "sticky", top: 0, background: "#fff" }}>{tableConfig.header}</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => (
                  <tr
                    key={`${row.name}-${row.depth}`}
                    style={{
                      borderBottom: "1px solid #f1f5f9",
                      background: row.hasChildren ? "#f8fafc" : "#fff",
                    }}
                  >
                    <td style={{ padding: "8px 12px", width: "180px", maxWidth: "180px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      <div style={{ display: "flex", alignItems: "center", paddingLeft: row.depth * 18, overflow: "hidden" }}>
                        {row.hasChildren ? (
                          <button
                            type="button"
                            onClick={() => toggleRow(row.name)}
                            style={{
                              border: "none",
                              background: "transparent",
                              cursor: "pointer",
                              padding: 0,
                              marginRight: 6,
                              width: 16,
                              color: "#64748b",
                              fontSize: 11,
                              flexShrink: 0,
                            }}
                            aria-label={expandedRows.has(row.name) ? "Collapse" : "Expand"}
                          >
                            {expandedRows.has(row.name) ? "▼" : "▶"}
                          </button>
                        ) : (
                          <span style={{ display: "inline-block", width: 16, marginRight: 6, flexShrink: 0 }} />
                        )}
                        <span style={{ fontWeight: row.hasChildren ? 700 : 500, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.name}>
                          {row.name}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: "8px 12px", width: "100%" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                        <div
                          style={{
                            height: 26,
                            background: "#f1f5f9",
                            borderRadius: 6,
                            overflow: "hidden",
                            display: "flex",
                            cursor: "pointer",
                            flex: 1,
                          }}
                          onMouseEnter={(e) =>
                            setHoverTip({ x: e.clientX, y: e.clientY, row })
                          }
                          onMouseMove={(e) =>
                            setHoverTip({ x: e.clientX, y: e.clientY, row })
                          }
                          onMouseLeave={() => setHoverTip(null)}
                        >
                          {row.total > 0 && (
                            <div
                              style={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                              }}
                            >
                              {(() => {
                                const pctA = (row.valA / row.total) * 100;
                                const pctB = (row.valB / row.total) * 100;
                                return (
                                  <>
                                    <div
                                      style={{
                                        width: `${pctA}%`,
                                        background: tableConfig.aColor,
                                        opacity: legendHover === "b" ? 0.2 : 1,
                                        transition: "opacity 0.15s",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#fff",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                        textShadow: "0 1px 2px rgba(0,0,0,0.35)",
                                        userSelect: "none",
                                      }}
                                    >
                                      {pctA >= 6 ? `${Math.round(pctA)}%` : ""}
                                    </div>
                                    <div
                                      style={{
                                        width: `${pctB}%`,
                                        background: tableConfig.bColor,
                                        opacity: legendHover === "a" ? 0.2 : 1,
                                        transition: "opacity 0.15s",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#fff",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                        textShadow: "0 1px 2px rgba(0,0,0,0.35)",
                                        userSelect: "none",
                                      }}
                                    >
                                      {pctB >= 6 ? `${Math.round(pctB)}%` : ""}
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                        {isPeakOffPeakTab && (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#334155",
                              minWidth: 85,
                              textAlign: "right",
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                            }}
                            title={`Total Energy: ${formatAmount(row.total)} ${tableConfig.unit}`}
                          >
                            {formatAmount(row.total)} {tableConfig.unit}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: 20, padding: "12px 0 0", fontSize: 12, color: "#475569" }}>
            <span
              onMouseEnter={() => setLegendHover("a")}
              onMouseLeave={() => setLegendHover(null)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
                opacity: legendHover === "b" ? 0.4 : 1,
                transition: "opacity 0.15s",
              }}
            >
              <span style={{ width: 12, height: 12, borderRadius: 3, background: tableConfig.aColor, display: "inline-block" }} />
              <FieldHelp id={tableConfig.aHelp}>{tableConfig.aLabel}</FieldHelp>
            </span>
            <span
              onMouseEnter={() => setLegendHover("b")}
              onMouseLeave={() => setLegendHover(null)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
                opacity: legendHover === "a" ? 0.4 : 1,
                transition: "opacity 0.15s",
              }}
            >
              <span style={{ width: 12, height: 12, borderRadius: 3, background: tableConfig.bColor, display: "inline-block" }} />
              <FieldHelp id={tableConfig.bHelp}>{tableConfig.bLabel}</FieldHelp>
            </span>
          </div>
        </div>
      ) : activeData.length > 0 ? (
        <div key="apex-view" style={{ padding: "0 8px 20px", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <div style={{ flex: 1, minHeight: 0, overflowY: isFullscreen ? "auto" : "visible", position: "relative", paddingLeft: "0" }}>
            <div
              style={{
                position: "absolute",
                left: "0",
                top: "50%",
                transform: "translateY(-50%) rotate(180deg)",
                transformOrigin: "center",
                writingMode: "vertical-lr",
                fontWeight: 600,
                color: "#475569",
                fontSize: "14px",
                pointerEvents: "none",
              }}
            >
              Machines
            </div>
            <Chart options={options} series={series} type="bar" height={isFullscreen ? Math.max(window.innerHeight - 100, displayData.length * 50) : 450} width="100%" />
          </div>
          {!isFullscreen && activeData.length > 10 && (
            <div style={{
              textAlign: "center",
              padding: "10px",
              fontSize: "12px",
              color: "#64748b",
              background: "#f8fafc",
              borderTop: "1px solid #f1f5f9",
              borderRadius: "0 0 16px 16px",
              fontWeight: 500
            }}>
              Showing top 10 machines. <span style={{ color: "#0ea5e9", fontWeight: 700 }}>Open full screen</span> to view all {activeData.length} machines.
            </div>
          )}
        </div>
      ) : (
        <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
          {isPeakOffPeakTab
            ? "No Peak/Off-Peak data available for the selected period."
            : "No ON/OFF data available for the selected period."}
        </div>
      )}
      <AIAnalyzerModal
        isOpen={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        chartPayload={aiModalPayload}
      />
    </div>
  );
};

export default OnOffChart;
